import { Folder } from '@mui/icons-material';
import Magnify from '@mui/icons-material/Search';
import { navigate } from '@reach/router';
import { Inspector } from '@virtus/components/Inspector';
import { LoadingIconSizes, LoadingIconType } from '@virtus/components/LoadingIcon/LoadingIcon';
import { Body, Page } from '@virtus/components/page';
import { CLOSE_TOAST, SHOW_ERROR, SHOW_MESSAGE } from 'actions/ErrorActions/errorActionTypes';
import { SET_HEIGHT_TO_REMOVE, SET_READONLY_HEADER } from 'actions/globalActions';
import ActionButton, { ButtonType, IconType } from 'components/Buttons/ActionButton';
import GridLayoutEdit from 'components/GridLayoutEdit';
import HeaderBizObject, { IHeaderBizObjectProps } from 'components/HeaderBizObject/HeaderBizObject';
import MultipleAlertModal from 'components/Modals/AlertModal/MultipleAlertModal';
import 'components/PageControl/PageControl.css';
import ToastNotification, { VirtusToast } from 'components/ToastNotification/ToastNotification';
import config from 'config/index';
import { getPath } from 'config/routeConfig';
import { RouteConstAccessDenied, RouteConstError, RouteConstNotFound } from 'constants/RouteConstants';
import { setUserPermissions } from 'helpers/AuthenticationHelper';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ToastProvider } from 'react-toast-notifications';
import { find } from 'reducers/Global';
import { IRoute } from 'reducers/routeFormatter';
import store from 'stores/configureStore';
import styled from 'styled-components';
import { PageMessageIconType } from 'util/enums/PageMessageIconTypeEnum';
import {
  ConvertDouble,
  getIconNameFromPath,
  getMenuIdByPath,
  getMenuInfo,
  getPageTitle,
} from 'util/helpers/helperFunctions';
import { PageTab } from './PageTab';

export interface IProps {
  appName?: string;
  pageTitle?: string;
  searchCriteria?: any;
  showSearch?: boolean;
  onSearchOpen?: any;
  onSearchClose?: any;
  errorMessage: string[];
  showError: boolean;
  inspectorTitle?: string;
  hideInspectorHelpIcon?: boolean;
  inspectorChildren?: React.ReactNode;
  headerBizObjectProps?: IHeaderBizObjectProps;
  children?: any;
  onExpandChange?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  pageWidth?: string;
  showInformation: boolean;
  errorHeader: string;
  closeAlertModal: any;
  setHeightToRemove: (value: number) => void;
  showToast: boolean;
  toastDetails: any;
  currentToast: any;
  closeToast: any;
  allowAlertMessageCopy: boolean;
  hideTabs?: boolean;
  pageHeaderComp?: React.ReactNode;
  setReadonlyHeader: (value: boolean) => void;
  showLayoutEdit: boolean;
  closeLayoutEdit: () => void;
  layoutInfo: any;
  hideLine?: boolean;
  preAuth?: boolean;
}

interface IState {
  isExpanded: boolean;
  heightToRemove: number;
}

const PageControl = (props: IProps) => {
  const [isExpanded, setIsExpanded] = useState(props.isExpanded || false);
  const [heightToRemove, setHeightToRemoveState] = useState(60);

  const [pageTitle, setPageTitle] = useState<string>('');
  const [tabList, setTabList] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  
  useEffect(() => {
    const initialize = async () => {
      try {
        if (
          window.location.pathname !== RouteConstNotFound &&
          window.location.pathname !== RouteConstError &&
          !window.location.pathname.startsWith('/DealList')
        ) {
          const menuIdValue = getMenuIdByPath(window.location.pathname);
          const menuInfo = getMenuInfo(ConvertDouble(menuIdValue)) || {};
          const menutType = menuInfo.type;
          if (menutType === 'T') {
            const currentMenuId = ConvertDouble(menuIdValue);
            const parentMenuId = menuInfo.parentId;
            const parentMenu = find(store.getState().globalRdcr.completeMenu, ConvertDouble(parentMenuId)) || {};
            let currentSelectedTab = 0;
            const newTabList = (parentMenu.subroutes || []).map((e: IRoute, index: number) => {
              if (ConvertDouble(e.menuId) === currentMenuId) {
                currentSelectedTab = index;
              }
              return {
                icon: getIcon(getIconNameFromPath(e.urlImage || '') || ''),
                path: e.fullPath,
                tabId: e.menuId,
                text: e.name,
              };
            });
            setTabList(newTabList);
            setSelectedTab(currentSelectedTab);
          }
          const authStatus = Boolean(menuIdValue);
          if (!authStatus && !props.preAuth) {
            navigate(RouteConstAccessDenied);
          } else {
            props.setReadonlyHeader(true);
            setPageTitle(getPageTitle(ConvertDouble(menuIdValue)));
            const defaultPathMap = store.getState().globalRdcr.defaultPathMap;
            setUserPermissions(defaultPathMap[ConvertDouble(menuIdValue) || 0].originalPath);
          }
        }
        setHeightToRemoveInternal();
      } catch (error) {
        //
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    if (props.isExpanded !== isExpanded) {
      setIsExpanded(props.isExpanded || false);
    }
  }, [props.isExpanded]);

  useEffect(() => {
    setHeightToRemoveInternal();
  }, [props.hideTabs, tabList.length]);

  const closeErrorModal = async () => {
    await props.closeAlertModal();
  };

  const openSearch = () => {
    props.onSearchOpen();
  };

  const setHeightToRemoveInternal = () => {
    let heightToRemoveValue = 60;

    if (tabList.length > 0 && !props.hideTabs) {
      heightToRemoveValue += 20;
    }
    if (props.headerBizObjectProps) {
      heightToRemoveValue += 15;
    }
    if (props.pageHeaderComp) {
      heightToRemoveValue += 30;
    }

    let gridHeightToRemove = heightToRemoveValue;
    if (props.searchCriteria) {
      gridHeightToRemove += 40;
    }
    setHeightToRemoveState(heightToRemoveValue);
    props.setHeightToRemove(gridHeightToRemove);
  };

  const closeSearch = () => {
    props.onSearchClose();
  };

  const onExpandChange = () => {
    setIsExpanded(!isExpanded);
  };

  let keys: any = [];
  if (props.searchCriteria) {
    keys = Object.keys(props.searchCriteria);
  }

  return (
    <Page appName={process.env.REACT_APP_APPNAME} pageTitle={pageTitle || props.pageTitle || ''}>
      <ToastProvider placement="bottom-right" autoDismiss={false} components={{ Toast: VirtusToast }}>
        {config.envColor && !props.hideLine && <EnvStrip />}
        <div style={{ width: props.pageWidth || '100%' }}>
          <div style={{ width: '100%', padding: '10px 10px 0px 10px' }}>
            {props.headerBizObjectProps && <HeaderBizObject {...props.headerBizObjectProps} />}
            {props.pageHeaderComp && props.pageHeaderComp}
            {tabList.length > 0 && !props.hideTabs && (
              <PageTab
                onChange={(data: any) => {
                  const path = data.path;
                  const newPath = getPath(path.slice(0), window.location.pathname, true);
                  navigate(newPath);
                }}
                dataSource={tabList}
                selectedIndex={selectedTab}
              />
            )}
          </div>
          <Body>
            <div
              id="genesisAppContent"
              style={{
                display: 'flex',
                width: '100%',
                padding: '0px 10px 0px 10px',
                height: `calc(100vh - ${heightToRemove}px)`,
              }}
            >
              <div style={{ width: '100%' }}>
                {props.searchCriteria && (
                  <div>
                    <table style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          {keys.length > 0 && (
                            <td>
                              {keys
                                .filter((item: any) => props.searchCriteria[item] !== '')
                                .map((item: any) => (
                                  <div key={item} className="search-criteria-items">
                                    <span
                                      style={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                      }}
                                    >
                                      {item + ': '}
                                    </span>
                                    <span style={{ color: 'var(--text)' }}>{props.searchCriteria[item]}</span>
                                  </div>
                                ))}
                            </td>
                          )}
                          <td style={{ verticalAlign: 'top' }}>
                            <div className="refine-search">
                              {props.showSearch ? (
                                <Magnify
                                  className="refine-search-icon"
                                  style={{ height: '20px' }}
                                  onClick={closeSearch}
                                />
                              ) : (
                                <ActionButton
                                  onClick={openSearch}
                                  buttonText="Refine Search"
                                  IconType={IconType.RefineSearch}
                                  buttonType={ButtonType.Page}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="page-content">
                  <div>{props.children}</div>
                </div>
              </div>
              {props.searchCriteria && props.inspectorTitle ? (
                <div
                  className={props.hideInspectorHelpIcon ? 'page-control--hide-help' : undefined}
                  style={{ boxSizing: 'border-box' }}
                >
                  <Inspector
                    onExpandChange={onExpandChange}
                    isExpanded={isExpanded || false}
                    title={props.inspectorTitle || props.pageTitle || ''}
                    onClose={closeSearch}
                    loading={false}
                    loadingIcon={{
                      type: LoadingIconType.Genesis,
                      size: LoadingIconSizes.large,
                    }}
                    open={props.showSearch}
                  >
                    {props.inspectorChildren}
                  </Inspector>
                </div>
              ) : (
                <></>
              )}
            </div>
          </Body>
        </div>
        {props.showError === true || props.showInformation ? (
          <MultipleAlertModal
            alertMessages={props.errorMessage}
            onCloseModal={closeErrorModal}
            headerText={props.errorHeader}
            iconType={props.showInformation ? PageMessageIconType.Information : PageMessageIconType.Warning}
            allowCopy={props.allowAlertMessageCopy}
          />
        ) : (
          <></>
        )}
        {
          <ToastNotification
            closeToast={props.closeToast}
            toastDetails={props.toastDetails}
            currentToast={props.currentToast}
          />
        }
        {props.showLayoutEdit && (
          <GridLayoutEdit
            onSave={props.closeLayoutEdit}
            onClose={props.closeLayoutEdit}
            parameters={props.layoutInfo}
          />
        )}
      </ToastProvider>
    </Page>
  );
};

const mapGlobalStateToProps = (stateFromStore: any) => {
  return {
    allowAlertMessageCopy: stateFromStore.errorRdcr.allowAlertCopy,
    currentToast: stateFromStore.errorRdcr.currentToast,
    errorHeader: stateFromStore.errorRdcr.errorHeader,
    errorMessage: stateFromStore.errorRdcr.errorMessage,
    layoutInfo: stateFromStore.errorRdcr.layoutInfo,
    showError: stateFromStore.errorRdcr.showError,
    showInformation: stateFromStore.errorRdcr.showInformation,
    showLayoutEdit: stateFromStore.errorRdcr.showLayoutEdit,
    showToast: stateFromStore.errorRdcr.showToast,
    toastDetails: stateFromStore.errorRdcr.toastDetails,
  };
};

const mapDispatchActionToProps = (dispatch: any) => {
  return {
    closeAlertModal: () => {
      dispatch({
        type: SHOW_ERROR,
        data: { errorMessage: '', showError: false },
      });
      dispatch({
        type: SHOW_MESSAGE,
        data: { errorMessage: '', showMessage: false, errorHeader: '' },
      });
    },
    closeLayoutEdit: () => {
      dispatch({ type: 'HIDE_LAYOUT_EDIT', data: {} });
    },
    closeToast: (id: string) => {
      dispatch({ type: CLOSE_TOAST, data: id });
    },
    setHeightToRemove: (value: number) => {
      dispatch({ type: SET_HEIGHT_TO_REMOVE, data: value });
    },
    setReadonlyHeader: (value: boolean) => {
      dispatch({ type: SET_READONLY_HEADER, data: value });
    },
  };
};

export default connect(mapGlobalStateToProps, mapDispatchActionToProps)(PageControl);

const EnvStrip: any = styled.div`
  position: fixed;
  z-index: 1;
  height: 3px;
  width: 100%;
  background-color: ${config.envColor};
`;

const getIcon = (iconName: string) => {
  switch (iconName.toUpperCase()) {
    case 'FOLDER':
    case 'FOLDER_OUT':
      return <Folder style={{ height: '16px' }} />;
    default:
      break;
  }
};
